import {
  ROOM_LABEL,
  assignKey,
  bindPrompt,
  formatKeyList,
  isBindableKey,
  keySetLabel,
  roomBlurb,
  sanitizeSettings,
  withDefaultKeys,
} from '../settings';
import type { KeySide, MoveDir, RoomKind, Settings } from '../settings';

const ROOMS: readonly RoomKind[] = ['person-computer', 'person-person', 'computer-computer'];
const PLAYABLE: readonly RoomKind[] = ['person-computer', 'computer-computer'];
type Screen = 'gate' | 'home' | 'multi' | 'match';

function overlayHidden(id: string): boolean {
  const el = document.getElementById(id);
  return el === null || el.hidden !== false;
}

export class HomeScreen {
  private readonly home = document.getElementById('home') as HTMLElement;
  private readonly settingsEl = document.getElementById('settings') as HTMLElement;
  private readonly volume = document.getElementById('set-volume') as HTMLInputElement;
  private readonly volumeLabel = document.getElementById('set-volume-label') as HTMLElement;
  private readonly mute = document.getElementById('set-mute') as HTMLInputElement;
  private readonly music = document.getElementById('set-music') as HTMLInputElement;
  private readonly stick = document.getElementById('set-stick') as HTMLInputElement;
  private readonly blastPreview = document.getElementById('set-blast-preview') as HTMLInputElement;
  private returnTo: Screen = 'gate';
  private current: Settings;
  private listening: { side: KeySide; dir: MoveDir } | null = null;
  private changed: (settings: Settings) => void = () => {};
  private soundOn: () => void = () => {};
  private matchView: (on: boolean) => void = () => {};

  constructor(settings: Settings) {
    this.current = settings;
  }

  bind(handlers: {
    onCreate: (room: RoomKind) => void;
    onMulti: () => void;
    onLeave: () => void;
    onChange: (settings: Settings) => void;
    onTestAudio: () => void;
    onSettingsVisibility: (open: boolean) => void;
    onMatchView: (on: boolean) => void;
  }): void {
    this.changed = handlers.onChange;
    this.soundOn = handlers.onTestAudio;
    this.matchView = handlers.onMatchView;
    for (const room of PLAYABLE) {
      document
        .getElementById(`room-${room}`)
        ?.addEventListener('click', () => handlers.onCreate(room));
    }
    document.getElementById('room-person-person')?.addEventListener('click', () => {
      handlers.onMulti();
    });
    for (const id of ['btn-multi-back', 'btn-auth-back']) {
      document.getElementById(id)?.addEventListener('click', () => {
        this.showHome();
      });
    }
    document.getElementById('btn-open-settings')?.addEventListener('click', () => {
      this.openSettings('home', handlers.onSettingsVisibility);
    });
    document.getElementById('btn-match-settings')?.addEventListener('click', () => {
      this.openSettings('match', handlers.onSettingsVisibility);
    });
    document.getElementById('btn-settings-back')?.addEventListener('click', () => {
      this.closeSettings(handlers.onSettingsVisibility);
    });
    document.getElementById('btn-home')?.addEventListener('click', () => handlers.onLeave());
    document.getElementById('btn-result-home')?.addEventListener('click', () => handlers.onLeave());

    const emit = () => {
      this.current = this.readForm();
      this.syncCopy();
      handlers.onChange(this.current);
    };
    this.volume.addEventListener('input', emit);
    this.mute.addEventListener('change', emit);
    this.music.addEventListener('change', emit);
    this.stick.addEventListener('change', emit);
    this.blastPreview.addEventListener('change', emit);
    for (const input of document.querySelectorAll<HTMLInputElement>('input[name="controls"]')) {
      input.addEventListener('change', emit);
    }
    for (const button of document.querySelectorAll<HTMLButtonElement>('.keycap')) {
      button.addEventListener('click', () => {
        const pad = button.closest<HTMLElement>('[data-side]');
        const side = pad?.dataset.side;
        const dir = button.dataset.dir;
        if (side !== 'bottom' && side !== 'top') return;
        if (dir !== 'up' && dir !== 'down' && dir !== 'left' && dir !== 'right') return;
        this.listening = { side, dir };
        button.blur();
        this.paintKeys();
      });
    }
    document.getElementById('btn-reset-keys')?.addEventListener('click', () => {
      this.listening = null;
      this.current = withDefaultKeys(this.current);
      this.fillForm();
      this.syncCopy();
      handlers.onChange(this.current);
    });
    window.addEventListener(
      'keydown',
      (e) => {
        if (!this.listening || this.settingsEl.hidden) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (e.repeat) return;
        if (e.code === 'Escape') {
          this.listening = null;
          this.paintKeys();
          return;
        }
        if (!isBindableKey(e.code)) return;
        this.current = assignKey(this.current, this.listening.side, this.listening.dir, e.code);
        this.listening = null;
        this.paintKeys();
        this.syncCopy();
        handlers.onChange(this.current);
      },
      true,
    );
    document.getElementById('btn-test-audio')?.addEventListener('click', () => {
      emit();
      handlers.onTestAudio();
    });
    for (const button of document.querySelectorAll<HTMLButtonElement>('.sound-toggle')) {
      button.addEventListener('click', () => {
        this.toggleSound();
        // A focused HUD button would also catch Space and Enter during the match.
        if (button.closest('#hud')) button.blur();
      });
    }

    window.addEventListener('keydown', (e) => {
      if (e.code !== 'Escape' || this.listening) return;
      if (!this.settingsEl.hidden) {
        this.closeSettings(handlers.onSettingsVisibility);
        return;
      }
      const auth = document.getElementById('auth');
      const lobby = document.getElementById('lobby');
      const multi = document.getElementById('multi');
      if ((auth && !auth.hidden) || (lobby && !lobby.hidden)) return;
      if (multi && !multi.hidden) {
        this.showHome();
        return;
      }
      if (this.home.hidden) handlers.onLeave();
    });

    this.fillForm();
    this.syncCopy();
    this.showBoot();
  }

  /** Flips mute from any screen. Settings keep the same value. */
  toggleSound(): void {
    this.current = sanitizeSettings({ ...this.current, muted: !this.current.muted });
    this.mute.checked = this.current.muted;
    this.syncCopy();
    this.changed(this.current);
    if (!this.current.muted) this.soundOn();
  }

  isMatchFocused(): boolean {
    return (
      this.home.hidden === true &&
      this.settingsEl.hidden === true &&
      overlayHidden('auth') &&
      overlayHidden('lobby') &&
      overlayHidden('multi')
    );
  }

  showBoot(): void {
    this.settingsEl.hidden = true;
    this.home.hidden = true;
    this.hideAccountScreens();
    const auth = document.getElementById('auth');
    if (auth) {
      auth.hidden = false;
      auth.setAttribute('aria-busy', 'true');
    }
    this.setAuthReady(false);
    this.setMatchInert(true);
  }

  showGate(): void {
    this.settingsEl.hidden = true;
    this.home.hidden = true;
    this.hideAccountScreens();
    const auth = document.getElementById('auth');
    if (auth) {
      auth.hidden = false;
      auth.removeAttribute('aria-busy');
    }
    this.setAuthReady(true);
    const signin = document.getElementById('auth-signin');
    const signup = document.getElementById('auth-signup');
    if (signin) signin.hidden = false;
    if (signup) signup.hidden = true;
    this.setMatchInert(true);
    this.setMatchChrome(false);
  }

  showHome(): void {
    this.settingsEl.hidden = true;
    this.hideAccountScreens();
    this.home.hidden = false;
    this.setMatchInert(true);
    this.setMatchChrome(false);
  }

  showMulti(): void {
    this.settingsEl.hidden = true;
    this.home.hidden = true;
    this.hideAccountScreens();
    const multi = document.getElementById('multi');
    if (multi) multi.hidden = false;
    this.setMatchInert(true);
    this.setMatchChrome(false);
  }

  showLobby(): void {
    this.settingsEl.hidden = true;
    this.home.hidden = true;
    this.hideAccountScreens();
    const lobby = document.getElementById('lobby');
    if (lobby) lobby.hidden = false;
    this.setMatchInert(true);
    this.setMatchChrome(false);
  }

  showMatch(): void {
    this.home.hidden = true;
    this.settingsEl.hidden = true;
    this.hideAccountScreens();
    this.setMatchInert(false);
    this.setMatchChrome(true);
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  }

  private openSettings(from: Screen, onVisibility: (open: boolean) => void): void {
    this.returnTo = from;
    this.fillForm();
    this.home.hidden = true;
    this.hideAccountScreens();
    this.settingsEl.hidden = false;
    this.setMatchInert(true);
    onVisibility(true);
  }

  private closeSettings(onVisibility: (open: boolean) => void): void {
    this.listening = null;
    this.settingsEl.hidden = true;
    if (this.returnTo === 'match') this.setMatchInert(false);
    else if (this.returnTo === 'multi') this.showMulti();
    else if (this.returnTo === 'home') this.showHome();
    else this.showGate();
    onVisibility(false);
  }

  private hideAccountScreens(): void {
    for (const id of ['auth', 'lobby', 'multi']) {
      const el = document.getElementById(id);
      if (el) el.hidden = true;
    }
  }

  /** Session check uses the same Account shell; hide forms until auth resolves. */
  private setAuthReady(ready: boolean): void {
    const boot = document.getElementById('auth-boot');
    if (boot) boot.hidden = ready;
    for (const id of ['auth-guest', 'btn-auth-back']) {
      const el = document.getElementById(id);
      if (el) el.hidden = !ready;
    }
    if (!ready) {
      const signin = document.getElementById('auth-signin');
      const signup = document.getElementById('auth-signup');
      if (signin) signin.hidden = true;
      if (signup) signup.hidden = true;
    }
  }

  private setMatchInert(inert: boolean): void {
    for (const id of ['hud', 'stage-wrap', 'dock']) {
      const el = document.getElementById(id);
      if (el) el.inert = inert;
    }
  }

  private setMatchChrome(on: boolean): void {
    document.getElementById('shell')?.classList.toggle('is-match', on);
    this.matchView(on);
  }

  private fillForm(): void {
    this.volume.value = String(Math.round(this.current.volume * 100));
    this.volumeLabel.textContent = this.volume.value;
    this.mute.checked = this.current.muted;
    this.music.checked = this.current.music;
    this.stick.checked = this.current.stickEnabled;
    this.blastPreview.checked = this.current.blastPreview;
    for (const input of document.querySelectorAll<HTMLInputElement>('input[name="controls"]')) {
      input.checked = input.value === this.current.controls;
    }
    this.paintKeys();
  }

  private paintKeys(): void {
    const hint = document.getElementById('bind-hint');
    if (hint) {
      const azerty =
        this.current.bottomKeys.up.includes('KeyZ') &&
        this.current.bottomKeys.left.includes('KeyQ');
      hint.textContent = this.listening
        ? bindPrompt(this.listening.side, this.listening.dir)
        : azerty
          ? 'W and A also start as Z and Q, for AZERTY.'
          : 'Each key belongs to one direction.';
    }
    for (const button of document.querySelectorAll<HTMLButtonElement>('.keycap')) {
      const pad = button.closest<HTMLElement>('[data-side]');
      const side = pad?.dataset.side;
      const dir = button.dataset.dir;
      if ((side !== 'bottom' && side !== 'top') || !dir) continue;
      if (dir !== 'up' && dir !== 'down' && dir !== 'left' && dir !== 'right') continue;
      const codes = this.current[side === 'bottom' ? 'bottomKeys' : 'topKeys'][dir];
      const listening = this.listening?.side === side && this.listening.dir === dir;
      button.classList.toggle('is-listening', listening);
      const named = formatKeyList(codes);
      const label = listening ? '…' : named;
      const small = document.createElement('small');
      small.textContent = dir;
      const strong = document.createElement('strong');
      strong.textContent = label;
      button.replaceChildren(small, strong);
      button.title = named;
      button.setAttribute(
        'aria-label',
        listening
          ? `Press a key for ${side} ${dir}`
          : `${side} ${dir}, currently ${named}. Click to change.`,
      );
    }
  }

  private readForm(): Settings {
    const selected = document.querySelector<HTMLInputElement>('input[name="controls"]:checked');
    return sanitizeSettings({
      volume: Number(this.volume.value) / 100,
      muted: this.mute.checked,
      music: this.music.checked,
      stickEnabled: this.stick.checked,
      blastPreview: this.blastPreview.checked,
      controls: selected?.value,
      bottomKeys: this.current.bottomKeys,
      topKeys: this.current.topKeys,
    });
  }

  private syncCopy(): void {
    this.volumeLabel.textContent = String(Math.round(this.current.volume * 100));
    const on = !this.current.muted;
    for (const button of document.querySelectorAll<HTMLButtonElement>('.sound-toggle')) {
      button.setAttribute('aria-pressed', String(on));
      button.title = on ? 'Sound on' : 'Sound off';
    }
    const bottom = keySetLabel(this.current.bottomKeys);
    const top = keySetLabel(this.current.topKeys);
    const solo = document.getElementById('blurb-solo');
    const shared = document.getElementById('blurb-shared');
    const swapped = document.getElementById('blurb-swapped');
    if (solo) {
      solo.textContent = `${bottom} and ${top} both move you at the bottom, with the stick. Used for single play.`;
    }
    if (shared) {
      shared.textContent = `Bottom: ${bottom} and the stick. Top: ${top}. Person vs computer still gives you both.`;
    }
    if (swapped) {
      swapped.textContent = `Bottom: ${top} and the stick. Top: ${bottom}. Person vs computer still gives you both.`;
    }
    for (const room of ROOMS) {
      const copy = document.getElementById(`copy-${room}`);
      const title = document.getElementById(`title-${room}`);
      if (copy) copy.textContent = roomBlurb(this.current, room);
      if (title) title.textContent = ROOM_LABEL[room];
    }
  }
}
